import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Card from "components/card";
import InputField from "components/fields/InputField";
import PasswordStrength from "components/fields/PasswordStrength";
import { getAdminProfile, updateAdminProfile, changeAdminPassword } from "api/adapters/auth/auth";
import { setAdminProfile } from "store/slices/authSlice";
import { validatePassword } from "utils/validators";
import { MdPerson, MdEmail, MdAdminPanelSettings, MdLock } from "react-icons/md";

// ─── Profile Details Section ────────────────────────────────────────────────
function ProfileDetails() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const reduxProfile = useSelector((state) => state.auth.adminProfile);

  // Fetch fresh profile from backend
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["adminProfile"],
    queryFn: getAdminProfile,
  });

  // Use fetched data if available, fall back to redux store
  const adminProfile = profileData?.data || reduxProfile;

  const [sUserName, setUserName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Sync form when profile data loads
  useEffect(() => {
    if (adminProfile?.sUserName) {
      setUserName(adminProfile.sUserName);
    }
  }, [adminProfile?.sUserName]);

  // Sync redux store when query returns fresh data
  useEffect(() => {
    if (profileData?.data) {
      dispatch(setAdminProfile(profileData.data));
    }
  }, [profileData, dispatch]);

  // Dirty check — display name only (avatar saves to browser storage immediately on pick)
  const originalName = adminProfile?.sUserName || "";
  const isDirty = sUserName.trim() !== originalName.trim();

  const { mutate: doUpdate, isPending } = useMutation({
    mutationFn: (payload) => updateAdminProfile(payload),
    onSuccess: (_res, variables) => {
      const prev = reduxProfile || {};
      dispatch(
        setAdminProfile({
          ...prev,
          sUserName: variables.sUserName,
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["adminProfile"] });
      setSuccess("Profile updated successfully.");
      setError("");
    },
    onError: (err) => {
      setError(err.message || "Failed to update profile.");
      setSuccess("");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sUserName.trim()) { setError("Name is required."); return; }
    setError("");
    setSuccess("");
    doUpdate({ sUserName: sUserName.trim() });
  };


  return (
    <Card extra="p-6">
      {/* Section Header */}
      <div className="mb-6 flex items-center gap-3 border-b border-gray-100 pb-4 dark:border-white/10">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-500/20">
          <MdPerson className="h-5 w-5 text-orange-500" />
        </div>
        <div>
          <h2 className="text-base font-bold text-navy-700 dark:text-white">Profile Details</h2>
          <p className="text-xs text-gray-400">Update your display name</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Avatar */}
        <div className="flex items-center gap-5">
          <div className="relative">
            {profileLoading && !adminProfile ? (
              <div className="h-20 w-20 animate-pulse rounded-2xl bg-gray-100 dark:bg-[#1a1a1a]" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 to-orange-500 ring-4 ring-orange-100 dark:ring-orange-500/20">
                <MdPerson className="h-12 w-12 text-white" />
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
              {adminProfile?.sUserName || "Admin"}
            </p>
            <p className="text-xs text-gray-400">{adminProfile?.eAdminType || "Super Admin"}</p>
          </div>
        </div>

        {/* Name field */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Display Name
          </label>
          {profileLoading ? (
            <div className="h-11 animate-pulse rounded-xl bg-gray-100 dark:bg-[#1a1a1a]" />
          ) : (
            <div className="relative">
              <MdPerson className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={sUserName}
                onChange={(e) => { setUserName(e.target.value); setError(""); setSuccess(""); }}
                placeholder="Enter your name"
                className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white dark:focus:border-orange-400"
              />
            </div>
          )}
        </div>

        {/* Email (read-only) */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Email Address
          </label>
          {profileLoading ? (
            <div className="h-11 animate-pulse rounded-xl bg-gray-100 dark:bg-[#1a1a1a]" />
          ) : (
            <div className="relative">
              <MdEmail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={adminProfile?.sEmail || ""}
                readOnly
                className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm text-gray-500 outline-none cursor-not-allowed dark:border-white/10 dark:bg-[#111111] dark:text-gray-400"
              />
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">Email cannot be changed.</p>
        </div>

        {/* Role (read-only) */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Role
          </label>
          {profileLoading ? (
            <div className="h-11 animate-pulse rounded-xl bg-gray-100 dark:bg-[#1a1a1a]" />
          ) : (
            <div className="relative">
              <MdAdminPanelSettings className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={adminProfile?.eAdminType || "Super Admin"}
                readOnly
                className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm text-gray-500 outline-none cursor-not-allowed dark:border-white/10 dark:bg-[#111111] dark:text-gray-400"
              />
            </div>
          )}
        </div>

        {error && <p className="text-sm font-medium text-red-500">{error}</p>}
        {success && <p className="text-sm font-medium text-green-500">{success}</p>}

        <button
          type="submit"
          disabled={!isDirty || isPending}
          className="w-full rounded-xl bg-orange-500 py-3 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-md shadow-orange-200 dark:shadow-none"
        >
          {isPending ? "Saving..." : "Save"}
        </button>
      </form>
    </Card>
  );
}

// ─── Change Password Section ─────────────────────────────────────────────────
function ChangePasswordSection() {
  const [sOldPassword, setSOldPassword] = useState("");
  const [sNewPassword, setSNewPassword] = useState("");
  const [sConfirmPassword, setSConfirmPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { mutate: changePassword, isPending } = useMutation({
    mutationFn: changeAdminPassword,
    onSuccess: (res) => {
      setSuccess(res.message || "Password updated successfully.");
      setError("");
      setSOldPassword("");
      setSNewPassword("");
      setSConfirmPassword("");
      setNewPasswordError("");
      setConfirmError("");
    },
    onError: (err) => {
      setError(err.message || "Failed to update password.");
      setSuccess("");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sOldPassword) { setError("Current password is required."); return; }
    const pErr = validatePassword(sNewPassword);
    if (pErr) { setNewPasswordError(pErr); return; }
    if (sNewPassword !== sConfirmPassword) { setConfirmError("Passwords do not match."); return; }
    setError("");
    setSuccess("");
    changePassword({ sOldPassword, sNewPassword, sConfirmPassword });
  };

  return (
    <Card extra="p-6">
      {/* Section Header */}
      <div className="mb-6 flex items-center gap-3 border-b border-gray-100 pb-4 dark:border-white/10">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-500/20">
          <MdLock className="h-5 w-5 text-orange-500" />
        </div>
        <div>
          <h2 className="text-base font-bold text-navy-700 dark:text-white">Change Password</h2>
          <p className="text-xs text-gray-400">Update your account password</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-1">
        <InputField
          variant="auth"
          extra="mb-3"
          label="Current Password*"
          placeholder="Enter current password"
          id="sOldPassword"
          type="password"
          value={sOldPassword}
          onChange={(e) => { setSOldPassword(e.target.value); setError(""); setSuccess(""); }}
        />

        <InputField
          variant="auth"
          extra="mb-1"
          label="New Password*"
          placeholder="Enter new password"
          id="sNewPassword"
          type="password"
          value={sNewPassword}
          state={newPasswordError ? "error" : undefined}
          onChange={(e) => {
            setSNewPassword(e.target.value);
            setNewPasswordError(validatePassword(e.target.value));
            setConfirmError("");
            setError("");
            setSuccess("");
          }}
        />
        {newPasswordError && (
          <p className="mb-1 ml-1.5 text-xs font-medium text-red-500">{newPasswordError}</p>
        )}
        <PasswordStrength password={sNewPassword} />

        <InputField
          variant="auth"
          extra="mb-1 mt-3"
          label="Confirm New Password*"
          placeholder="Re-enter new password"
          id="sConfirmPassword"
          type="password"
          value={sConfirmPassword}
          state={confirmError ? "error" : sConfirmPassword && sConfirmPassword === sNewPassword ? "success" : undefined}
          onChange={(e) => {
            setSConfirmPassword(e.target.value);
            setConfirmError(e.target.value !== sNewPassword ? "Passwords do not match." : "");
            setError("");
            setSuccess("");
          }}
        />
        {confirmError && (
          <p className="mb-2 ml-1.5 text-xs font-medium text-red-500">{confirmError}</p>
        )}
        {!confirmError && sConfirmPassword && sConfirmPassword === sNewPassword && (
          <p className="mb-2 ml-1.5 text-xs font-medium text-green-500">Passwords match.</p>
        )}
        {!confirmError && (!sConfirmPassword || sConfirmPassword !== sNewPassword) && <div className="mb-2" />}

        {error && <p className="mb-2 text-sm font-medium text-red-500">{error}</p>}
        {success && <p className="mb-2 text-sm font-medium text-green-500">{success}</p>}

        <button
          type="submit"
          disabled={isPending || !!newPasswordError || !!confirmError || !sOldPassword || !sNewPassword || !sConfirmPassword}
          className="mt-2 w-full rounded-xl bg-orange-500 py-3 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-md shadow-orange-200 dark:shadow-none"
        >
          {isPending ? "Updating..." : "Update Password"}
        </button>
      </form>
    </Card>
  );
}

// ─── Profile Page ─────────────────────────────────────────────────────────────
const ProfilePage = () => {
  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <ProfileDetails />
      <ChangePasswordSection />
    </div>
  );
};

export default ProfilePage;
